// ════════════════════════════════════════════════════════════
//  RingsideScreen.jsx — the DOM over the Ringside scene
// ════════════════════════════════════════════════════════════
//  Opened from a League bout that's waiting (soon / prep). Shows who's
//  fighting, the clock, Willie's caption, the bet panel — and sends you
//  into the fight itself a few seconds before the bell, so nobody misses
//  the start.
//
//  ⚙ top right: Willie's commentary on/off (caP'n: "eventually can turn
//  off/on commentary") and fireworks on/off. Both remembered per browser.
//  Commentary is captions for now — no voice, no sound effects until
//  caP'n makes them (Music Law).
// ════════════════════════════════════════════════════════════
import { arenaOfBout } from "../three/Arena.jsx";
import { useEffect, useRef, useState } from "react";
import Ringside, { SIDE_COLOR } from "../three/Ringside.jsx";
import { BetPanel, SENT } from "./Online.jsx";
import { fbNet as net } from "../online/net.js";
import { serverNow } from "../online/ie.js";
import { loadHour, parseBoutId } from "../online/stage.js";
import { boutLabel } from "../online/circuit.js";
import { LINES } from "../data/roster.js";
import { FINAL_COUNT } from "../data/ringside.js";
import SkyControls from "./SkyControls.jsx";
import { BOX, boxInside } from "../online/skybox.js";
import { skyCamDefault } from "../three/BattleCamera.jsx";
import { say } from "../data/text.js";

const FINAL_MS = (FINAL_COUNT + 2) * 1000;

const LS = "shatterproof";
const readLS = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}"); } catch { return {}; } };
const writeLS = (v) => { try { localStorage.setItem(LS, JSON.stringify({ ...readLS(), ...v })); } catch { /* private mode: fine */ } };
const clock = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
export const HANDOFF_MS = 6500;   // into the fight a beat before the bell

// bt_{hour}_{j} → the hour
const hourOfId = (id) => (parseBoutId(id) || { h: Number(String(id).split("_")[1]) }).h;

export default function RingsideScreen({ on, fightId, onWatch, onBack }) {
  const [H, setH] = useState(null);
  const [, tick] = useState(0);
  const [prefs, setPrefs] = useState(() => { const p = readLS(); return { commentary: p.commentary !== false, fireworks: p.fireworks !== false }; });
  const [gear, setGear] = useState(false);
  const [betOpen, setBetOpen] = useState(false);
  const director = useRef({ leftMs: null, names: null });
  const cue = useRef({});
  const handed = useRef(false);
  // THE SKY BOX at ringside (caP'n 2026-10-20: "When I go ringside I should be able to go immediately into
  // the sky box"): one button; remembered, so the next ringside opens where you were; it carries into the fight.
  const [sky, setSky] = useState(() => { const w = BOX.wantView === "sky"; BOX.wantView = null; return w || readLS().ringSky === true; });
  const userCam = useRef({ view: null });
  useEffect(() => {
    const uc = userCam.current;
    uc.view = sky ? "sky" : null;
    if (sky) skyCamDefault(uc);
  }, [sky]);
  useEffect(() => { boxInside(true); }, []);   // your body is in the box at ringside too: the button below only changes the camera
  const intoFight = useRef(false);   // handed into the fight IN the box: stay inside (you keep your seat; friends never see you blink out)
  useEffect(() => () => { if (!intoFight.current) boxInside(false); }, []);
  const toggleSky = () => setSky((v) => { writeLS({ ringSky: !v }); return !v; });
  useEffect(() => {   // V switches views, as it does in the fight (here: the Sky Box ↔ the ringside camera)
    const down = (e) => {
      const t = e.target;
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) || BOX.ui.modal) return;
      const k = e.key.toLowerCase();
      if (k === "v") toggleSky();
      else if (k === "r" && userCam.current.view === "sky") skyCamDefault(userCam.current);   // R: back behind you, facing the plate
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // the hour: every 4 s (the board's own rhythm), every 1 s near the bell so
  // the fight is found the moment its seed lands
  const bellAt = useRef(null);
  useEffect(() => {
    let alive = true, running = false, last = 0;
    const load = async () => {
      if (running) return; running = true; last = Date.now();
      try { const h = await loadHour(net, { hour: hourOfId(fightId), now: serverNow() }); if (alive && h) setH(h); }
      catch (e) { console.warn("[ringside]", e); } // eslint-disable-line no-console
      finally { running = false; }
    };
    load();
    const id = setInterval(() => {
      const near = bellAt.current != null && bellAt.current - serverNow() < FINAL_MS;
      if (Date.now() - last >= (near ? 1000 : 4000)) load();
    }, 500);
    return () => { alive = false; clearInterval(id); };
  }, [fightId]);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 250); return () => clearInterval(id); }, []);

  const f = H ? H.bouts.find((x) => x.id === fightId) : null;
  const now = serverNow();
  // what the clock is counting to, in plain words
  let left = null, label = "Loading the fight…";
  if (f) {
    if (f.phase === "live") label = "It's on!";
    else if (f.phase === "done") label = "That one's over";
    else if (f.phase === "scratched") label = "Scratched — the hour ran out";
    else if (f.A && f.B && f.startAt != null) { left = f.startAt - now; label = "Bell in"; }
    else label = "On after the fight before it";   // back to back: its bell is the end of the one before + ringside
  }
  const names = f && f.A && f.B ? { A: f.A.name, B: f.B.name } : null;
  // THE BELL: known once the fight before it is decided (stage.js) — Willie counts down to it
  const bell = f && f.A && f.B && f.phase !== "done" && f.startAt != null ? f.startAt : null;
  bellAt.current = bell;
  director.current = { leftMs: bell != null ? bell - now : null, names };

  // the hand-off: into the fight a moment before the bell (or straight away if it's already on)
  useEffect(() => {
    if (!f || handed.current) return;
    const go = H && H.locked && ((f.A && f.B && f.phase !== "done" && f.startAt != null && f.startAt - now <= HANDOFF_MS) || f.phase === "live");
    if (go && !SENT.has(f.id)) {
      const fighting = !!(on && on.authUid && ((f.A && f.A.uid === on.authUid) || (f.B && f.B.uid === on.authUid)));   // a guest walking out for their own bout: no box
      handed.current = true; SENT.add(f.id); BOX.wantView = sky && !fighting ? "sky" : null; intoFight.current = sky && !fighting;
      Promise.resolve(onWatch(f.id)).then((ok) => { if (ok === false) { BOX.wantView = null; intoFight.current = false; handed.current = false; } });   // it didn't open: try again, nothing stale left behind
    }
  });

  const set = (k) => setPrefs((p) => { const n = { ...p, [k]: !p[k] }; writeLS({ [k]: n[k] }); return n; });
  const teams = f && f.A && f.B ? [f.A.team, f.B.team] : [[], []];
  const line = cue.current.line;

  return (
    <div className={"ringside" + (sky ? " in-sky" : "")}>
      <Ringside director={director} cue={cue} teams={teams} fireworks={prefs.fireworks} arena={arenaOfBout(f)} sky={sky} userCam={userCam} />
      <div className="rs-top">
        <button className="btn ghost tiny" onClick={onBack}>← Board</button>
        <div className="rs-card">
          {names ? <>
            <span className="rs-who" style={{ color: SIDE_COLOR[0] }}>{names.A}</span>
            <span className="rs-vs">vs</span>
            <span className="rs-who" style={{ color: SIDE_COLOR[1] }}>{names.B}</span>
          </> : <span className="rs-who">The League</span>}
          {H && f && <small>{boutLabel(f)}</small>}
        </div>
        <button className={"btn tiny" + (sky ? " primary" : " ghost")} onClick={toggleSky} title="Wait for the bell in your Sky Box (and stay there when the fight starts)">{say("frontrow.view.sky")}</button>
        <button className="rs-gear" aria-label="Settings" onClick={() => setGear((g) => !g)}>⚙</button>
        {gear && (
          <div className="rs-gearmenu">
            <label><input type="checkbox" checked={prefs.commentary} onChange={() => set("commentary")} /> Willie's commentary</label>
            <label><input type="checkbox" checked={prefs.fireworks} onChange={() => set("fireworks")} /> Fireworks</label>
          </div>
        )}
      </div>
      <div className="rs-clock">
        <span>{label}</span>
        {left != null && <b>{clock(left)}</b>}
      </div>
      {f && f.A && f.B && (
        <div className="rs-trios">
          {[f.A, f.B].map((x, s) => (
            <div key={s} className={"rs-trio s" + s}>
              {x.team.map((id) => <span key={id}>{LINES[id]?.name || id}</span>)}
            </div>
          ))}
        </div>
      )}
      {prefs.commentary && line && <div className="rs-say"><b>Willie</b>{line}</div>}
      {sky && <div className="fr-bar"><SkyControls /></div>}
      {f && f.A && f.B && !f.open && (f.phase === "soon" || f.phase === "prep") && (
        <div className={"rs-bet" + (betOpen ? " open" : "")}>
          <button className="btn tiny primary" onClick={() => setBetOpen((o) => !o)}>{betOpen ? "Hide bets" : "Bet"}</button>
          {betOpen && <BetPanel on={on} path={f.path} pool={`${f.path}/bets_${f.A.key}_${f.B.key}`} names={[f.A.name, f.B.name]} lockAt={f.startAt ?? null}
            keys={[f.A.key, f.B.key]} fighters={[f.A.uid, f.B.uid].filter(Boolean)} />}
        </div>
      )}
    </div>
  );
}
